import React from 'react';

function FactsAndFiguresSection() {
  const [started, setStarted] = React.useState(false);
  const [vals, setVals] = React.useState({ n250: 0, n300: 0, n15: 0, n99: 0, n100: 0 });
  const sectionRef = React.useRef(null);

  React.useEffect(function () {
    if (!sectionRef.current) return;
    var obs = new IntersectionObserver(
      function (entries) {
        if (entries[0].isIntersecting && !started) setStarted(true);
      },
      { threshold: 0.3 }
    );
    obs.observe(sectionRef.current);
    return function () { obs.disconnect(); };
  }, [started]);

  React.useEffect(function () {
    if (!started) return;
    var duration = 2000, steps = 60, step = duration / steps;
    var t = 0;
    var id = setInterval(function () {
      t += step;
      var p = Math.min(t / duration, 1);
      var ease = 1 - Math.pow(1 - p, 2);
      setVals({
        n250: Math.round(250 * ease),
        n300: Math.round(300 * ease),
        n15: Math.round(15 * ease),
        n99: Math.round(99 * ease),
        n100: Math.round(100 * ease)
      });
      if (t >= duration) clearInterval(id);
    }, step);
    return function () { clearInterval(id); };
  }, [started]);

  const styles = {
    section: {
      width: '100%', maxWidth: 1920, minHeight: 960, background: '#E73123', opacity: 1,
      display: 'flex', alignItems: 'center', padding: '80px 48px',
      boxSizing: 'border-box'
    },
    inner: {
      display: 'flex', flexDirection: 'column', width: '100%', maxWidth: 1824,
      margin: 0, alignItems: 'flex-start'
    },
    subheadingRow: {
      display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8
    },
    subheading: {
      fontFamily: 'Lato', fontWeight: 400, fontSize: '26px', lineHeight: '100%',
      letterSpacing: '-0.01em', textTransform: 'uppercase', color: '#FFFFFF', margin: 0
    },
    dashLine: {
      width: 150, height: 0, borderTop: '2px solid #FFFFFF', flexShrink: 0
    },
    title: {
      fontFamily: 'Lato', fontWeight: 700, fontSize: '34px', lineHeight: '40px',
      letterSpacing: 0, textTransform: 'uppercase', color: '#FFFFFF', margin: '0 0 24px'
    },
    paragraph: {
      fontFamily: 'Lato', fontWeight: 400, fontSize: 16, lineHeight: '26px',
      letterSpacing: 0, color: '#FFFFFF', margin: '0 0 32px', maxWidth: 700,
      wordWrap: 'break-word', overflowWrap: 'break-word'
    },
    statsGrid: {
      display: 'flex', gap: 80, alignItems: 'flex-start',
      flexWrap: 'wrap'
    },
    statsCol: {
      display: 'flex', flexDirection: 'column', gap: 32,
      alignItems: 'flex-start'
    },
    statBlock: {
      display: 'flex', flexDirection: 'column', gap: 8,
      alignItems: 'flex-start',
      animation: 'factsFloat 4s ease-in-out infinite'
    },
    statValue: {
      fontFamily: 'Lato', fontWeight: 700, fontSize: '34px', lineHeight: '40px',
      letterSpacing: 0, textTransform: 'uppercase', color: '#FFFFFF', margin: 0
    },
    statLabel: {
      fontFamily: 'Lato', fontWeight: 400, fontSize: 16, lineHeight: '26px',
      letterSpacing: 0, color: '#FFFFFF', margin: 0
    }
  };

  var paragraph = 'Our impact is defined by measurable results, consistent performance, and a proven track record of excellence. From the number of projects successfully executed to the scale of infrastructure supported, our journey reflects strength, reliability, and sustained growth. Each milestone represents our commitment to precision engineering and long-term value creation.';

  var blockStyle = function (delay) {
    return Object.assign({}, styles.statBlock, { animationDelay: delay + 's' });
  };

  return React.createElement(React.Fragment, null,
    React.createElement('style', null,
      '@keyframes factsFloat { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }'
    ),
    React.createElement('section', { ref: sectionRef, style: styles.section, className: 'facts-figures-section' },
    React.createElement('div', { style: styles.inner, className: 'facts-inner' },
      React.createElement('div', { style: styles.subheadingRow },
        React.createElement('h2', { style: styles.subheading }, 'FACTS & FIGURES'),
        React.createElement('div', { style: styles.dashLine })
      ),
      React.createElement('h3', { style: styles.title }, 'OUR IMPACT AT A GLANCE'),
      React.createElement('p', { style: styles.paragraph }, paragraph),
      React.createElement('div', { style: styles.statsGrid, className: 'facts-stats-grid' },
        React.createElement('div', { style: styles.statsCol },
          React.createElement('div', { style: blockStyle(0) },
            React.createElement('p', { style: styles.statValue }, vals.n250 + '+'),
            React.createElement('p', { style: styles.statLabel }, 'Projects Executed')
          ),
          React.createElement('div', { style: blockStyle(0.3) },
            React.createElement('p', { style: styles.statValue }, vals.n300 + '+'),
            React.createElement('p', { style: styles.statLabel }, 'Skilled Professionals')
          ),
          React.createElement('div', { style: blockStyle(0.6) },
            React.createElement('p', { style: styles.statValue }, vals.n15 + '+'),
            React.createElement('p', { style: styles.statLabel }, 'Years of Experience')
          )
        ),
        React.createElement('div', { style: styles.statsCol },
          React.createElement('div', { style: blockStyle(0.2) },
            React.createElement('p', { style: styles.statValue }, 'PAN INDIA'),
            React.createElement('p', { style: styles.statLabel }, 'Operational Presence')
          ),
          React.createElement('div', { style: blockStyle(0.5) },
            React.createElement('p', { style: styles.statValue }, vals.n99 + '%'),
            React.createElement('p', { style: styles.statLabel }, 'Client Satisfaction')
          ),
          React.createElement('div', { style: blockStyle(0.8) },
            React.createElement('p', { style: styles.statValue }, vals.n100 + '%'),
            React.createElement('p', { style: styles.statLabel }, 'Safety Commitment')
          )
        )
      )
    )
  )
  );
}

export default FactsAndFiguresSection;
